import * as React from 'react';
import { InvoieGuideSteps } from '../invoices/NewInvoice';
import { ProgramGuideSteps } from '../program/program-guide/program-guide';
import { SeasonGuideSteps } from '../program/season-guide/season-guide';
import { RegistrationSteps } from 'components/registration/v2/types';
import { get } from 'lodash';

export type CrumbValidation = {
  key: string;
  not?: string | string[];
  equals?: string | boolean;
};

export enum BreadcrumbStatus {
  DEFAULT,
  CURRENT,
  COMPLETED,
}

export type BreadcrumbSection = {
  name: string;
  children: BreadcrumbItem[];
  hideChildren?: boolean;
  requiredData?: Array<CrumbValidation | string>;
  isValid?: boolean;
  isDisabled?: boolean;
  hidden?: boolean;
  onClick?: () => void;
};

type BreadcrumbItem = {
  id?: ProgramGuideSteps | SeasonGuideSteps | RegistrationSteps | InvoieGuideSteps;
  name: string;
  requiredData?: Array<CrumbValidation | string>;
  isValid?: boolean;
  hidden?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
};

type BreadcrumbProps = {
  current: ProgramGuideSteps | SeasonGuideSteps | RegistrationSteps | InvoiceGuideSteps;
  breadcrumb: BreadcrumbSection[];
  data: any;
};

const checkIsComplete = (data, required: Array<CrumbValidation | string>) => {
  const isValidArray = [];

  required.forEach((field: string | CrumbValidation) => {
    if (
      typeof field === 'object' &&
      field.hasOwnProperty('key') &&
      (field.hasOwnProperty('equals') || field.hasOwnProperty('not'))
    ) {
      if (field.hasOwnProperty('not') && Array.isArray(field.not)) {
        isValidArray.push(
          field.not.every((i) => i.toString().trim() != String(get(data, field.key)).toString().trim())
        );
      } else {
        isValidArray.push(get(data, field.key).toString().trim() != String(field.not).toString().trim());
      }

      if (field.hasOwnProperty('equals')) {
        isValidArray.push(field.equals.toString().trim() == get(data, field.key).toString().trim());
      }
    }

    if (typeof field === 'string') {
      if (get(data, field) && Array.isArray(get(data, field)) && get(data, field).length > 0) {
        isValidArray.push(true);
      } else if (get(data, field) && !Array.isArray(get(data, field))) {
        isValidArray.push(true);
      } else if (!get(data, field) && !Array.isArray(get(data, field))) {
        isValidArray.push(false);
      } else if (get(data, field) && Array.isArray(get(data, field)) && get(data, field).length <= 0) {
        isValidArray.push(false);
      }
    }
  });

  return !isValidArray.includes(false);
};

export const getBreadcrumb = (breadcrumb, nextstep, data) => {
  let foundChildIndex;
  let foundParentIndex;
  const newbreadcumb = [];

  breadcrumb.forEach((parent, parentIndex) => {
    const newparent = {
      ...parent,
      status: BreadcrumbStatus.DEFAULT,
      children: [],
      isCompleted: parent.requiredData || parent.hasOwnProperty('isValid') ? false : true,
    };

    if (newparent.requiredData) {
      newparent.isCompleted = checkIsComplete(data, newparent.requiredData);
    } else if (parent.hasOwnProperty('isValid')) {
      newparent.isCompleted = newparent.isValid;
    }

    parent.children.forEach((crumb, childIndex) => {
      const child = {
        ...crumb,
        status: BreadcrumbStatus.DEFAULT,
        isCompleted: crumb.requiredData || crumb.hasOwnProperty('isValid') ? false : true,
      };

      if (crumb.requiredData) {
        child.isCompleted = checkIsComplete(data, crumb.requiredData);
      } else if (crumb.hasOwnProperty('isValid')) {
        child.isCompleted = crumb.isValid;
      } else {
        if (crumb.name === nextstep) {
          child.isCompleted = false;
        }
      }

      if (crumb.name === nextstep || crumb?.id === nextstep) {
        foundChildIndex = childIndex;
        foundParentIndex = parentIndex;
        child.status = BreadcrumbStatus.CURRENT;
      }

      if ((foundChildIndex < childIndex && foundParentIndex === parentIndex) || foundParentIndex < parentIndex) {
        child.status = BreadcrumbStatus.DEFAULT;
        if (!child.requiredData && !crumb.hasOwnProperty('isValid')) {
          child.isCompleted = false;
        }
      }

      newparent.children.push(child);
    });

    if (foundParentIndex === parentIndex) {
      newparent.status = BreadcrumbStatus.CURRENT;

      if (!newparent.requiredData && !parent.hasOwnProperty('isValid')) {
        newparent.isCompleted = false;
      }
    }

    newbreadcumb.push(newparent);
  });

  return newbreadcumb;
};

const Breadcrumb = ({ current, breadcrumb, data }: BreadcrumbProps) => {
  const breadcrumbState = React.useMemo(() => getBreadcrumb(breadcrumb, current, data), [current, data, breadcrumb]);

  const renderButton = (item) =>
    item.onClick ? (
      <button
        data-testid={`${item.name.replace(/ /g, '')}-breadcrumb-button`}
        type="button"
        onClick={item.onClick}
        disabled={item.isDisabled ? true : false}
      >
        <span className="item-text">{item.name}</span>
      </button>
    ) : (
      <span className="item-text">{item.name}</span>
    );

  return (
    <div className="program-guide-breadcrumb u-borderRight u-borderNeutral4">
      <ul className="program-guide">
        {breadcrumbState.map((e) => (
          <li
            key={e.name}
            data-testid={'breadcrumb-item-' + e.name.toLowerCase().replace(/\s/g, '-')}
            className={`program-guide-item program-guide-item--heading
              ${e.status === BreadcrumbStatus.CURRENT ? 'program-guide-item--active' : ''}
              ${e.isCompleted ? 'program-guide-item--complete' : ''}
              ${e.hidden ? 'program-guide-item--hidden' : ''}
            `}
          >
            {e.isCompleted ? (
              <svg className="completed-checkmark" width="23" height="24" viewBox="0 0 23 24" fill="none">
                <path
                  d="M22.0547 5.39844L11.2383 16.2148C10.832 16.6211 10.1211 16.6211 9.71484 16.2148L6.61719 13.168C6.21094 12.7109 6.21094 12.0508 6.61719 11.6445C7.07422 11.1875 7.73438 11.1875 8.14062 11.6445L10.4766 13.9297L20.5312 3.875C20.9375 3.41797 21.6484 3.41797 22.0547 3.875C22.5117 4.28125 22.5117 4.94141 22.0547 5.39844ZM16.0625 1.94531C14.2344 1.23438 12.1523 0.980469 10.0195 1.33594C5.60156 2.14844 2.09766 5.70312 1.33594 10.1211C0.117188 17.4336 6.16016 23.6797 13.4727 22.7148C17.7383 22.207 21.3438 19.0078 22.4609 14.793C22.918 13.2188 22.918 11.6953 22.7148 10.2227C22.5625 9.35938 21.4961 9.05469 20.8359 9.66406C20.582 9.91797 20.4805 10.2734 20.582 10.6289C20.7852 12.0508 20.6836 13.625 19.9727 15.25C18.7539 18.1953 16.0117 20.3281 12.8125 20.6328C7.32812 21.1406 2.70703 16.4688 3.41797 10.8828C3.875 7.07422 6.97266 3.92578 10.7812 3.41797C12.6602 3.16406 14.4375 3.51953 15.9609 4.28125C16.3672 4.48438 16.875 4.43359 17.2305 4.07812C17.7383 3.57031 17.6367 2.70703 16.9766 2.35156C16.6719 2.25 16.3672 2.09766 16.0625 1.94531Z"
                  fill="#199A50"
                />
              </svg>
            ) : (
              <svg className="incompleted-complete" width="22" height="22" viewBox="0 0 22 22" fill="none">
                <circle cx="11" cy="11" r="10" stroke="#CBD0D6" strokeWidth="2" />
              </svg>
            )}

            {renderButton(e)}
            {e.children && e.children.length > 0 && (
              <ul className={`program-guide-list ${e.hideChildren ? 'program-guide-list--hidden' : ''}`}>
                {e.children.map((c) => (
                  <li
                    key={c.name}
                    data-testid={'breadcrumb-item-' + c.name.toLowerCase().replace(/\s/g, '-')}
                    className={`program-guide-item
                      ${c.status === BreadcrumbStatus.CURRENT ? 'program-guide-item--active' : ''}
                      ${c.isCompleted ? 'program-guide-item--complete' : ''}
                      ${c.hidden ? 'program-guide-item--hidden' : ''}
                    `}
                  >
                    {renderButton(c)}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Breadcrumb;
